import { useRouteMatch, useRouter } from '@funtech-int/rival-react';
import React, { FC, Suspense, useState } from 'react';
import classnames from 'classnames';
import { RouterLocation } from '@funtech-int/rival';
import {
    AnalyticsSenderProvider,
    createPictureFactory,
    useAnalytics,
} from '@funtech-int/yango-shared';

import { checkIsTvSeries } from '@/shared/lib/movie';
import { useOnMount } from '@/shared/hooks/useOnMount';
import { clientOnlyPage } from '@/shared/lib/clientOnly';
// TODO: отказаться от прямого импорта когда перейдем на @/scenarios/player https://st.yandex-team.ru/SAFT-8848
import { PlayerManager } from '@/scenarios/legacy-player/components/PlayerManager/PlayerManagerMain';
import { AutoOpenPaymentWidget } from '@/shared/components/AutoOpenPaymentWidget/AutoOpenPaymentWidget';

import { filmRoute } from '../../routes';
import { useMoviePage } from '../../hooks/useMoviePage';
import { useMovieCard } from '../../hooks/useMovieCard';
import { MovieNotFoundError } from '../../lib/error';
import { MovieInfo } from '../MovieInfo/MovieInfo';
import { MovieCardContentActions } from '../MovieCardContentActions/MovieCardContentActions';
import { MovieCardSkeleton } from '../MovieCardSkeleton/MovieCardSkeleton';
import { BackgroundTrailer } from '../BackgroundTrailer/BackgroundTrailer';
import { OverviewTab } from '../Overview/Overview';
import { Tab, Tabs } from '../Tabs/Tabs';
import { useI18n } from '../../hooks/useI18n';
import { createMovieCardAnalytics } from '../../lib/movieCardAnalytics';
import { SeriesStructure } from '../SeriesStructure/SeriesStructure';
import { MoviePageHead } from '../MoviePageHead/MoviePageHead';

import styles from './MovieCard.module.css';

const badgeFactory = createPictureFactory({
    fallback: {
        src: '210x120',
    },
});

type MovieTabId = 'series' | 'overview';

interface MovieCardContentProps {
    contentId: string;
    className?: string;
    location: RouterLocation<any, any>;
}

export const MovieCardContent: FC<MovieCardContentProps> = ({ contentId, className, location }) => {
    const router = useRouter();
    const i18n = useI18n();
    const movieCardAnalytics = useAnalytics(createMovieCardAnalytics);

    const [activeTab, setActiveTab] = useState<MovieTabId>('overview');

    const tabs: Tab<MovieTabId>[] = [
        { id: 'overview' as const, label: i18n('movie_tabs__overview') },
        { id: 'series' as const, label: i18n('movie_tabs__seasons_episodes') },
    ];

    const { movie } = useMovieCard({ contentId });

    const { img: badgeImg } = badgeFactory({
        fallbackUrl: movie?.rightholderLogo.logo?.toString(),
    });

    useOnMount(() => {
        movieCardAnalytics.movieCardShowed(contentId);
        movieCardAnalytics.movieCardImpressionShowed(contentId, movie?.title.toString() ?? '');
    });

    if (!movie) {
        throw new MovieNotFoundError(router.serialize(location));
    }

    const renderTabContent = () => {
        switch (activeTab) {
            case 'overview':
                return (
                    <OverviewTab
                        id={contentId}
                        createSelectionAnalytics={movieCardAnalytics.createSelectionAnalytics}
                    />
                );
            case 'series':
                return (
                    <SeriesStructure
                        filmId={contentId}
                        movieTitle={movie.title.toString()}
                        createSerialStructureAnalytics={
                            movieCardAnalytics.createSerialStructureAnalytics
                        }
                    />
                );
            default:
                // eslint-disable-next-line no-case-declarations
                const _exhaustiveCheck: never = activeTab;
                return _exhaustiveCheck;
        }
    };

    const hasSeasons = checkIsTvSeries(movie.typename);

    return (
        <>
            <div className={classnames(styles.root, className)}>
                <div className={styles.background}>
                    <BackgroundTrailer
                        contentId={movie.promoTrailerContentId}
                        title={movie.title.toString()}
                        backgroundUrl={movie.cover?.toString()}
                    />
                    {badgeImg.src && <img src={badgeImg.src} alt="" className={styles.badge} />}
                </div>
                <div className={styles.gradientContainer}>
                    <div className={styles.backdrop} />
                    <div className={styles.gradient} />
                </div>
                <MovieInfo movie={movie} />
                <div
                    className={classnames(styles.contentActions, {
                        [styles.contentActionsSeries]: hasSeasons,
                    })}
                >
                    <MovieCardContentActions
                        contentId={contentId}
                        analytics={movieCardAnalytics.createMovieContentActionsAnalytics(movie)}
                    />
                </div>
                {hasSeasons && (
                    <Tabs
                        className={styles.tabs}
                        activeTab={activeTab}
                        onChange={setActiveTab}
                        tabs={tabs}
                    />
                )}
            </div>
            <div className={styles.content}>{renderTabContent()}</div>
        </>
    );
};

const MovieCardPage = clientOnlyPage(() => {
    const location = useRouteMatch(filmRoute);
    const contentId = location.params.id;

    return (
        <>
            <PlayerManager />
            <AnalyticsSenderProvider>
                <Suspense fallback={<MovieCardSkeleton />}>
                    <MovieCardContent
                        key={contentId}
                        contentId={contentId}
                        className={styles.moviePageRoot}
                        location={location}
                    />
                    <AutoOpenPaymentWidget />
                </Suspense>
            </AnalyticsSenderProvider>
        </>
    );
});

export const Movie: FC = () => {
    const { contentId, movie, movieWebPage } = useMoviePage();

    return (
        <>
            <MoviePageHead contentId={contentId} movie={movie} movieWebPage={movieWebPage} />
            <MovieCardPage />
        </>
    );
};

export default Movie;
